/**
 * Layout share modal — Share / Import the structural layout (counts + always
 * selection) as a short string, so two friends can sync the same template.
 *
 * Importing wipes filled-in hint data; only the empty layout is replaced.
 */
import { useMemo, useState } from 'react';
import { X, Share2, Copy, Check } from 'lucide-react';
import { useTracker } from '@/contexts/tracker-context';
import { encodeLayout, decodeLayout } from '@/lib/tracker-layout';

interface LayoutShareModalProps {
  onClose: () => void;
}

export function LayoutShareModal({ onClose }: LayoutShareModalProps) {
  const { state, dispatch } = useTracker();
  const [tab, setTab] = useState<'share' | 'import'>('share');
  const [copied, setCopied] = useState(false);
  const [importInput, setImportInput] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  const encoded = useMemo(() => {
    const sometimesKinds = Array.from({ length: state.nbSometimes }, (_, i) =>
      state.sometimesHints[i]?.kind ?? 'regular'
    );
    const startingItems = state.items
      .filter((i) => i.isObtained || i.state > 0)
      .map((i) => (i.state > 0 ? { id: i.id, state: i.state } : { id: i.id }));
    return encodeLayout({
      nbGoals: state.nbGoals,
      nbSometimes: state.nbSometimes,
      nbBarren: state.nbBarren,
      isWoth: state.isWoth,
      alwaysHintNames: state.alwaysHintNames,
      sometimesKinds,
      startingItems,
    });
  }, [
    state.nbGoals,
    state.nbSometimes,
    state.nbBarren,
    state.isWoth,
    state.alwaysHintNames,
    state.sometimesHints,
    state.items,
  ]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(encoded);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — user can still select+copy manually
    }
  };

  const handleImport = () => {
    const layout = decodeLayout(importInput);
    if (!layout) {
      setImportError('Invalid layout string. Make sure you pasted the full code.');
      return;
    }
    dispatch({ type: 'APPLY_LAYOUT', layout });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Share2 size={16} />
            Share Tracker Layout
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setTab('share')}
            className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
              tab === 'share'
                ? 'text-zelda-gold border-b-2 border-zelda-gold'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Share
          </button>
          <button
            onClick={() => setTab('import')}
            className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
              tab === 'import'
                ? 'text-zelda-gold border-b-2 border-zelda-gold'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Import
          </button>
        </div>

        {tab === 'share' ? (
          <div className="p-4 flex flex-col gap-3">
            <p className="text-xs text-gray-400">
              Copy this string and send it to a friend. It carries the layout
              and starting inventory ({state.nbGoals} {state.isWoth ? 'WotH' : 'paths'},{' '}
              {state.nbBarren} barren, {state.nbSometimes} sometimes,{' '}
              {state.alwaysHintNames.length} always,{' '}
              {state.items.filter((i) => i.isObtained || i.state > 0).length} items) — not your
              filled-in hints.
            </p>
            <textarea
              readOnly
              value={encoded}
              className="w-full h-28 bg-gray-800 text-white text-xs font-mono px-3 py-2 rounded border border-gray-600 outline-none focus:border-zelda-gold resize-none break-all"
              onFocus={(e) => e.currentTarget.select()}
            />
            <div className="flex justify-end">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30 rounded hover:bg-zelda-gold/30"
              >
                {copied ? (
                  <>
                    <Check size={14} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} /> Copy
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-3">
            <p className="text-xs text-gray-400">
              Paste a layout string from a friend. Importing replaces the current
              counts and always list, and clears all filled hint data.
            </p>
            <textarea
              value={importInput}
              onChange={(e) => {
                setImportInput(e.target.value);
                setImportError(null);
              }}
              placeholder="zr-layout-v1.…"
              className="w-full h-28 bg-gray-800 text-white text-xs font-mono px-3 py-2 rounded border border-gray-600 outline-none focus:border-zelda-gold resize-none break-all"
            />
            {importError && <p className="text-xs text-red-400">{importError}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!importInput.trim()}
                className="px-3 py-1.5 text-xs font-medium bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30 rounded hover:bg-zelda-gold/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Apply Layout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
